import React, { useState, useMemo } from "react";
import {
  Button,
  Text,
  XStack,
  YStack,
  Separator,
  Spinner,
  ScrollView,
} from "tamagui";

import { HubWithLocation } from "src/contexts/AppSyncContext";

interface HubsOverviewProps {
  hubs: any[];
  hubsLoading: boolean;
  hubsError: any;
  selectedHub: HubWithLocation | null;
  fetchHubs: () => void;
  handleSelectHub: (hubId: string) => void;
}

type SortDirection = "asc" | "desc";

export const HubsOverview: React.FC<HubsOverviewProps> = ({
  hubs,
  hubsLoading,
  hubsError,
  selectedHub,
  fetchHubs,
  handleSelectHub,
}) => {
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSort = () => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  };

  const sortedHubs = useMemo(
    () =>
      [...hubs].sort((a, b) => {
        const aValue = a.name || "Unnamed";
        const bValue = b.name || "Unnamed";

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      }),
    [hubs, sortDirection],
  );

  const renderHeader = () => (
    <XStack justifyContent="space-between" alignItems="center">
      <Text fontSize="$4" fontWeight="bold">
        Hubs
      </Text>
      <Button onPress={() => fetchHubs()} size="$2" theme="blue">
        Fetch All Hubs
      </Button>
    </XStack>
  );

  const renderLoadingState = () => (
    <YStack alignItems="center" padding="$2">
      <Spinner size="small" />
      <Text marginTop="$1" fontSize="$2">
        Loading hubs...
      </Text>
    </YStack>
  );

  const renderErrorState = () => (
    <Text color="$red10">Error loading hubs: {hubsError.message}</Text>
  );

  const renderEmptyState = () => <Text>No hubs found.</Text>;

  const renderStats = () => (
    <XStack justifyContent="space-between">
      <Text>Total Hubs: {hubs.length}</Text>
      <Text>
        Active: {hubs.filter((hub) => hub.status === "ACTIVE").length}
      </Text>
    </XStack>
  );

  const renderColumnHeaders = () => (
    <XStack gap="$1">
      <Button size="$1" theme="gray" flex={1} onPress={() => handleSort()}>
        <Text fontSize="$1" fontWeight="bold">
          Name {sortDirection === "asc" ? "↑" : "↓"}
        </Text>
      </Button>
    </XStack>
  );

  const renderRow = (hub: any) => (
    <Button
      key={hub.id}
      paddingVertical="$2"
      paddingHorizontal="$3"
      width="100%"
      height="auto"
      justifyContent="flex-start"
      theme={selectedHub?.id === hub.id ? "active" : "gray"}
      onPress={() => handleSelectHub(hub.id)}
    >
      <Text fontSize="$2" textAlign="left" flexWrap="wrap">
        {hub.name || "Unnamed"}
      </Text>
    </Button>
  );

  const renderHubList = () => (
    <ScrollView style={{ maxHeight: 150 }}>
      <YStack alignItems="flex-start" gap="$1" marginTop="$1">
        {sortedHubs.map(renderRow)}
      </YStack>
    </ScrollView>
  );

  const renderSelectedHubDetails = () => (
    <YStack gap="$1">
      <Text fontWeight="bold" fontSize="$4" marginVertical="$2">
        Selected Hub:
      </Text>
      {selectedHub && (
        <>
          <Text fontSize="$1">Name: {selectedHub.name || "None"}</Text>
          <Text fontSize="$1">ID: {selectedHub.id}</Text>
          <Text fontSize="$1">MAC Address: {selectedHub.macAddress}</Text>
          <Text fontSize="$1">S/N: {selectedHub.serialNumber}</Text>
          <Text fontSize="$1">Status: {selectedHub.status}</Text>
          <Text fontSize="$1">Location: {selectedHub.location}</Text>
        </>
      )}
    </YStack>
  );

  const renderContent = () => {
    if (hubsLoading) return renderLoadingState();
    if (hubsError) return renderErrorState();
    if (hubs.length === 0) return renderEmptyState();

    return (
      <>
        {renderStats()}
        <Separator />
        {renderColumnHeaders()}
        {renderHubList()}
        <Separator />
        {renderSelectedHubDetails()}
      </>
    );
  };

  return (
    <YStack
      backgroundColor="$backgroundHover"
      padding="$3"
      borderRadius="$4"
      gap="$2"
    >
      {renderHeader()}
      <Separator />
      {renderContent()}
    </YStack>
  );
};
